import styled from "@emotion/styled";
import { FC, useState } from "react";
import { Field, FieldRenderProps } from "react-final-form";

import { FormField, Input, SpacingToken, Switch, Text } from "@livechat/design-system-react-components";

import { Divider } from "components/divider/Divider";
import Flex from "components/layout/Flex";
import { IFormBlockEmail } from "store/entities/contact-forms/interfaces";
import { withVar } from "themes/helpers";

import { EMAIL_INCORRECT_MESSAGE, GENERIC_REQUIRED_MESSAGE } from "../constants";

interface IProps {
  name: string;
}

export const EmailBlock: FC<IProps> = ({ name }) => {
  return <Field name={name} render={({ input, meta }) => <EmailBlockInput input={input} meta={meta} />} />;
};

const EmailBlockInput: FC<FieldRenderProps<IFormBlockEmail>> = ({ input, meta }) => {
  return (
    <Flex column>
      <Field name={`${input.name}.value`}>
        {({ input, meta }) => (
          <FormField labelText="Email address label" error={meta.error}>
            <Input {...input} error={meta.error} data-testid="email-input-label" />
          </FormField>
        )}
      </Field>
      <ErrorFields input={input} meta={meta} />
    </Flex>
  );
};

const ErrorFields: FC<FieldRenderProps<IFormBlockEmail>> = ({ input, meta }) => {
  const areErrorsEqualToDefault =
    input.value.requiredError === GENERIC_REQUIRED_MESSAGE && input.value.incorrectError === EMAIL_INCORRECT_MESSAGE;

  const [isCustomError, setIsCustomError] = useState(!areErrorsEqualToDefault);

  const [rememberedRequiredError, setRememberedRequiredError] = useState<string>(
    input.value.requiredError || GENERIC_REQUIRED_MESSAGE
  );
  const [rememberedIncorrectError, setRememberedIncorrectError] = useState<string>(
    input.value.incorrectError || EMAIL_INCORRECT_MESSAGE
  );

  const handleToggleCustomError = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const value = event.target.checked;
    if (!value) {
      setIsCustomError(false);
      input.onChange({
        ...input.value,
        requiredError: GENERIC_REQUIRED_MESSAGE,
        incorrectError: EMAIL_INCORRECT_MESSAGE,
      });
    } else {
      setIsCustomError(true);
      input.onChange({
        ...input.value,
        requiredError: rememberedRequiredError,
        incorrectError: rememberedIncorrectError,
      });
    }
  };

  const handleChangeRequiredError = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setRememberedRequiredError(event.target.value);
    input.onChange({ ...input.value, requiredError: event.target.value });
  };

  const handleChangeIncorrectError = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setRememberedIncorrectError(event.target.value);
    input.onChange({ ...input.value, incorrectError: event.target.value });
  };

  return (
    <>
      <Divider />
      <Flex
        marginV={withVar(SpacingToken.Spacing2)}
        gap={withVar(SpacingToken.Spacing2)}
        alignItems="center"
        onClick={() => {
          setIsCustomError(!isCustomError);
        }}
      >
        <Switch size="medium" on={isCustomError} onChange={handleToggleCustomError} />
        <Text>Custom error message</Text>
      </Flex>
      {isCustomError && (
        <>
          <ErrorFormField labelText="Error message content for missing email address" error={meta.error?.requiredError}>
            <Input
              error={meta.error?.requiredError}
              value={input.value.requiredError}
              onChange={handleChangeRequiredError}
            />
          </ErrorFormField>
          <ErrorFormField
            labelText="Error message content for incorrect email address"
            error={meta.error?.incorrectError}
          >
            <Input
              error={meta.error?.incorrectError}
              value={input.value.incorrectError}
              onChange={handleChangeIncorrectError}
            />
          </ErrorFormField>
        </>
      )}
    </>
  );
};

const ErrorFormField = styled(FormField)`
  margin-bottom: ${withVar(SpacingToken.Spacing4)};
`;
